import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import CPageModule from '../../../components/CPageModule';
import CPageModulePaginatedTable from '../../../components/CPageModulePaginatedTable';
import CStatefulContainer from '../../../components/CStatefulContainer';
import Page from '../../../components/layout/Page';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { type IAugmentedBackendResponse } from '../../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../../models/IRequestParams';
import type { IResponse } from '../../../models/IResponse';
import { type ISortingMeta } from '../../../models/ISortingMeta';
import { type TPandasTable } from '../../../models/TPandasTable';
import { SvFactsheetLinksServices } from '../../../services/SvFactsheetLinksServices';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasTable>> & IRequestParams;
}

export default function PFactsheetInquiryLinkIndex() {
  const { locale } = useAppSelector((state) => state.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageInquiryHeading = CLocalizedTextString('pubFsInquirylinkAllH1');

  const defaultSorting: ISortingMeta = { sortCol: 0, ascending: 'descending' };

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: { isLoading: true, isError: false },
      params: { sorting: defaultSorting }
    }
  });

  const loadFactsheetInquiryLinks = async (page: number, sorting?: ISortingMeta, search?: string) => {
    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      { snapshot: (await getState()).snapshot },
      setState,
      async () =>
        await SvFactsheetLinksServices.getFactsheetInquiryLinksSnapshot({
          page,
          sorting: sorting ?? defaultSorting,
          search
        })
    );
    updateStateAttribute<State>(
      { snapshot: { ...(await getState()).snapshot, params: { sorting, search } } },
      setState
    );
  };

  useEffect(() => {
    (async () => {
      await loadFactsheetInquiryLinks(
        Number.parseInt(searchParams.get('page') ?? '1'),
        {
          sortCol: Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString()),
          ascending: (searchParams.get('ascending') as unknown as ISortingMeta['ascending']) ?? 'descending'
        },
        searchParams.get('search_text') ?? undefined
      );
    })();
  }, [locale]);

  return (
    <Page title={pageInquiryHeading}>
      <CPageModule>
        <h1>{pageInquiryHeading}</h1>
      </CPageModule>
      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CStatefulContainer submodule apiRequestState={state.snapshot.state} showContent={!!state.snapshot.data}>
          <CPageModulePaginatedTable
            state={state.snapshot.state}
            data={state.snapshot.data}
            params={state.snapshot.params}
            searchBarName={'inquiryLinks'}
            searchBarPlaceholder={CLocalizedTextString('pubFactsheetInquirySearchPlaceholder')}
            colSpanArr={[1, 1.25, 1.25, 2, 3, 1, 0]}
            colAlignmentArr={['left', 'left', 'left', 'left', 'left', 'center']}
            colFormatArr={[{in: 'tzTimestamp', out: 'localeDateString'}, undefined, undefined, undefined, undefined, undefined]}
            onChange={async (page, sorting, search) => {
              await loadFactsheetInquiryLinks(page, sorting, search);
              searchParams.set('page', page.toString());
              searchParams.set('sortCol', sorting.sortCol.toString());
              searchParams.set('ascending', sorting.ascending);
              searchParams.set('search', search);
              setSearchParams(searchParams);
            }}
            rowLinks={Object.values(state.snapshot.data?.results?.ID?? []).map((e) => `${e}`)}
          />
        </CStatefulContainer>
      </CPageModule>
    </Page>
  );
}
